import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, Check, Phone } from "lucide-react";
import { SERVICES, IMAGES, BUSINESS } from "../data/site";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";
import LeadForm from "../components/LeadForm";
import { Reveal } from "../components/Reveal";

const BENEFITS = ["Free no-obligation survey", "Fixed, transparent pricing", "10-year workmanship guarantee", "Premium materials only", "Fully insured & qualified", "Clean & tidy team"];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const Icon = service ? (Icons[service.icon] || Icons.Home) : Icons.Home;

  useEffect(() => {
    document.title = service
      ? `${service.title} | Sterling Roofworks`
      : "Service | Sterling Roofworks";
    window.scrollTo(0, 0);
  }, [service]);

  if (!service) {
    return (
      <>
        <Navbar />
        <div className="container-lux pt-40 pb-40 text-center">
          <h1 className="font-head text-4xl uppercase">Service not found</h1>
          <Link to="/" className="btn-gold rounded-sm px-6 py-3 inline-block mt-6">Back Home</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <FloatingActions waTopic={service.wa} />
      <section className="relative pt-36 pb-20 overflow-hidden grain" data-testid="service-detail">
        <div className="absolute inset-0">
          <img src={service.img} alt={service.title} className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/70 to-[#0D0D0D]" />
        </div>
        <div className="container-lux relative">
          <Link to="/#services" data-testid="service-back" className="inline-flex items-center gap-2 text-sm text-[#C8C8C8] hover:text-[#D4AF37] transition-colors mb-8">
            <ArrowLeft size={16} /> All Services
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="w-16 h-16 rounded-sm glass flex items-center justify-center mb-6">
              <Icon size={30} strokeWidth={1.4} className="text-[#D4AF37]" />
            </div>
            <p className="overline mb-3">Roofing Service</p>
            <h1 className="font-head text-5xl sm:text-6xl uppercase tracking-tighter font-bold">{service.title}</h1>
            <p className="mt-5 max-w-2xl text-lg text-[#C8C8C8]">{service.blurb} Serving Stockport, Manchester, Cheshire and surrounding areas with premium craftsmanship.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#0D0D0D]">
        <div className="container-lux grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-head text-3xl uppercase tracking-tight mb-6">Why choose Sterling for {service.title.toLowerCase()}?</h2>
            <p className="text-[#C8C8C8] leading-relaxed mb-8">
              With over 20 years of experience and 500+ roofs completed, our family-run team delivers {service.title.toLowerCase()} to the highest standard.
              Every project starts with a free survey and a detailed fixed quotation — no hidden costs, no pressure.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {BENEFITS.map((b) => (
                <div key={b} className="flex items-center gap-3 glass rounded-sm p-4">
                  <Check size={16} className="text-[#D4AF37] shrink-0" strokeWidth={2} />
                  <span className="text-sm">{b}</span>
                </div>
              ))}
            </div>
            <a href={BUSINESS.phoneHref} className="btn-ghost rounded-sm px-6 py-4 mt-8 inline-flex items-center gap-2 text-sm">
              <Phone size={16} strokeWidth={1.5} /> Call {BUSINESS.phone}
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="glass rounded-md p-6 sm:p-8 sticky top-28">
              <h3 className="font-head text-2xl uppercase tracking-tight mb-2">Get a Free Quote</h3>
              <p className="text-sm text-[#C8C8C8] mb-5">For {service.title.toLowerCase()} and more.</p>
              <LeadForm source={`service-${service.slug}`} compact />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-[#1B1B1B]">
        <div className="container-lux">
          <p className="overline mb-6">Other Services</p>
          <div className="flex flex-wrap gap-3">
            {SERVICES.filter((s) => s.slug !== slug).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="font-head uppercase tracking-wider text-xs px-4 py-2 rounded-full border border-white/15 text-[#C8C8C8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
